import type { MultiPolygon, Polygon, Position } from "geojson";
import { polygonParts } from "./clip";

/**
 * Metres per degree of longitude and latitude at `lat`.
 *
 * A local flat metric: good to a fraction of a percent over a field, and far cheaper than a real
 * projection. Longitude is scaled because a degree of it shrinks towards the poles — at Iowa's
 * latitude it covers about three quarters of what a degree of latitude does, so measuring in raw
 * degrees would stretch every east-west distance.
 */
export function metresPerDegree(lat: number): { x: number; y: number } {
	return { x: 111_320 * Math.max(Math.cos((lat * Math.PI) / 180), 1e-6), y: 110_540 };
}

/** Area enclosed by one ring, in m², by the shoelace formula. Always positive. */
export function ringAreaSqM(ring: Position[], scale: { x: number; y: number }): number {
	let sum = 0;
	for (let i = 0; i < ring.length - 1; i++) {
		sum += ring[i][0] * scale.x * (ring[i + 1][1] * scale.y) - ring[i + 1][0] * scale.x * (ring[i][1] * scale.y);
	}
	return Math.abs(sum / 2);
}

/** Area of a polygon in m², holes subtracted. */
export function polygonAreaSqM(geometry: Polygon | MultiPolygon | null, scale: { x: number; y: number }): number {
	if (!geometry) return 0;
	let total = 0;
	for (const part of polygonParts(geometry)) {
		// Ring 0 is the outer ring; the rest are holes and come off the total.
		part.coordinates.forEach((ring, ringIndex) => {
			total += (ringIndex === 0 ? 1 : -1) * ringAreaSqM(ring, scale);
		});
	}
	return total;
}

/** The first coordinate of a polygon — a stable anchor for picking the local metric. */
export function anchorOf(geometry: Polygon | MultiPolygon): Position {
	return polygonParts(geometry)[0].coordinates[0][0];
}

/** Squared distance from `p` to segment a→b, in the local metric. */
function distance2ToSegment(p: Position, a: Position, b: Position, scale: { x: number; y: number }): number {
	const px = p[0] * scale.x;
	const py = p[1] * scale.y;
	const ax = a[0] * scale.x;
	const ay = a[1] * scale.y;
	const vx = b[0] * scale.x - ax;
	const vy = b[1] * scale.y - ay;
	const len2 = vx * vx + vy * vy;
	const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / len2));
	const dx = px - (ax + t * vx);
	const dy = py - (ay + t * vy);
	return dx * dx + dy * dy;
}

/** Shortest distance from `p` to any edge of `geometry`, in metres. */
export function distanceToEdges(
	p: Position,
	geometry: Polygon | MultiPolygon,
	scale: { x: number; y: number },
): number {
	let best = Infinity;
	for (const part of polygonParts(geometry)) {
		for (const ring of part.coordinates) {
			for (let i = 0; i < ring.length - 1; i++) {
				best = Math.min(best, distance2ToSegment(p, ring[i], ring[i + 1], scale));
			}
		}
	}
	return Math.sqrt(best);
}
